import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { annotateIds } from '../../actions/Annotations';
import { annotatePlaylists } from '../../actions/Playlist';
import { clickedShareOption } from '../../actions/Share';
import QATags from '../../constants/QATags';
import { COPY, FACEBOOK, TWITTER } from '../../constants/ShareModal';
import { PandoraTypes } from '../../constants/SourceTypes';
import { getInDomain } from '../../domains/externalUtils';
import { getCurrentListenerFromState } from '../../lib/ListenerUtils';
import { getShareDeepLinkData } from '../../lib/RouteUtils/CoreRouteUtils';
import * as ShareUtils from '../../lib/ShareUtils';
import * as FacebookService from '../../lib/Social/FacebookService';
import { getBranchInstance } from '../../tools/Globals';
import GridItem from '../lib/GridItem/GridItem';

import ShareOptions from './ShareOptions';

import './ShareTabContent.scss';

class ShareTabContent extends React.Component {
  static propTypes = {
    tabObject: PropTypes.object.isRequired,
    isActive: PropTypes.bool.isRequired,
    isAdBlockerEnabled: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    showShareCoachmark: PropTypes.func.isRequired,
    clickedShareOption: PropTypes.func,
    fullSourcePath: PropTypes.string,
    shareURLData: PropTypes.object,
    annotateIds: PropTypes.func.isRequired,
    annotatePlaylists: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.shouldWaitForBranch = false;
    this.state = {
      shareURL: '',
      branchError: null,
    };
  }

  componentDidMount() {
    const { sourceId, sourceType, tabContent } = this.props.tabObject;
    if (ShareUtils.needAnnotation(tabContent)) {
      if (sourceType === PandoraTypes.PLAYLIST) {
        this.props.annotatePlaylists({ pandoraIds: [sourceId] });
      } else {
        this.props.annotateIds([sourceId], false);
      }
    }

    this.setShareURL(this.props.fullSourcePath);
  }

  componentDidUpdate(prevProps) {
    if (this.props.shareURLData && !prevProps.shareURLData) {
      this.setShareURL(this.props.fullSourcePath);
    }
  }

  componentWillUnmount() {
    this.shouldWaitForBranch = false;
  }

  setShareURL = fullSourcePath => {
    this.setState({
      shareURL: fullSourcePath,
    });

    if (!this.props.isAdBlockerEnabled) {
      this.createBranchLink(fullSourcePath);
    }
  };

  createBranchLink = fullSourcePath => {
    this.shouldWaitForBranch = true;
    const branch = getBranchInstance();
    const data = this.props.shareURLData;

    branch.link({ data }, (err, link) => {
      if (!this.shouldWaitForBranch) return;
      if (err) {
        this.setState({
          shareURL: fullSourcePath,
          branchError: err,
        });
      } else {
        this.setState({
          shareURL: link,
        });
      }
    });
  };

  onSocialClick = platform => {
    const { shareURL } = this.state;
    const { tabObject } = this.props;
    const { sourceType, tabContent, sourceId } = tabObject;
    const { mainText, creatorName, creatorTwitterHandle } = tabContent;

    if (platform === TWITTER) {
      const message = ShareUtils.getTwitterBoilerplateHelper(
        this.props,
        sourceType,
        mainText,
        creatorName,
        shareURL,
        creatorTwitterHandle,
      );
      ShareUtils.openTwitterComposer(message);
      this.props.clickedShareOption({
        twitter: true,
        twitterHandle: creatorTwitterHandle,
        shareMethod: platform.toLowerCase(),
        facebook: false,
        sourceId,
      });
    } else if (platform === FACEBOOK) {
      FacebookService.openFacebookShareDialog(shareURL);
      this.props.clickedShareOption({
        facebook: true,
        shareMethod: platform.toLowerCase(),
        twitter: false,
        sourceId,
      });
    } else if (platform === COPY) {
      this.props.clickedShareOption({
        shareMethod: platform.toLowerCase(),
        facebook: false,
        twitter: false,
        sourceId,
      });
    }
  };

  renderSource = (sourceType, tabContent) => {
    const isPlaceholder = !tabContent.mainText;
    const isCircle = sourceType === PandoraTypes.ARTIST || PandoraTypes.CURATOR;
    return (
      <GridItem
        isCircle={isCircle}
        isPlaceholder={isPlaceholder}
        undoHover={true}
        useDefaultCursor={true}
        imgUrl={tabContent.imgUrl}
        mainText={tabContent.mainText}
        secondText={tabContent.secondText}
        thirdText={tabContent.thirdText}
        qaMainText={QATags.SMBMT}
      />
    );
  };

  renderShareOptions = () => {
    const { shareURL } = this.state;
    return (
      <ShareOptions
        onSocialClick={this.onSocialClick}
        onClose={this.props.onClose}
        shareSourcePath={shareURL}
        showShareCoachmark={this.props.showShareCoachmark}
        clickedShareOption={this.props.clickedShareOption}
      />
    );
  };

  render() {
    const { isActive } = this.props;
    const { sourceType, tabContent } = this.props.tabObject;
    const className = isActive ? 'ShareTabContent' : 'ShareTabContent visuallyHidden';
    return (
      <div className={className}>
        {this.renderSource(sourceType, tabContent)}
        {this.renderShareOptions()}
      </div>
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const props = {};
  const { tabObject } = ownProps;
  if (!tabObject) return props;

  const { sourceId, sourceType, tabContent } = tabObject;
  const listener = getCurrentListenerFromState(state);
  const listenerId = listener ? listener.get('listenerId') : null;
  // The application host is hardcoded here to allow branch to scrape pages even in test environments
  const applicationHost = 'https://www.pandora.com';

  const fullSourcePath =
    tabContent.sourcePath && listenerId
      ? `${applicationHost}${tabContent.sourcePath}?part=ug-desktop&corr=${listenerId}`
      : null;
  props.fullSourcePath = fullSourcePath;
  props.isAdBlockerEnabled = getInDomain('application.utils.isAdBlockerDetected', false)(state);

  if (fullSourcePath && sourceType) {
    const data = {
      sourceType,
      sourcePath: tabContent.sourcePath,
      shareURL: fullSourcePath,
      imgUrl: tabContent.imgUrl,
      // genreStationName is only used in STATION type
      // Check getShareDeepLinkData -> getMetaTagDetailsForURL -> getMetaDetails
      genreStationName: sourceType === PandoraTypes.STATION ? tabContent.mainText : null,
    };
    props.shareURLData = getShareDeepLinkData(state, data);
  }

  props.tabObject = ShareUtils.generateShareTabObject(state, sourceId, sourceType);

  return props;
}

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      annotateIds,
      annotatePlaylists,
      clickedShareOption,
    },
    dispatch,
  );
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ShareTabContent));
